import { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import {
  LogOut, RefreshCcw, Mail, Phone as PhoneIcon, Car, Home as HomeIcon,
  Building2, Download, Users, Inbox, KeyRound, Trash2, MessageSquare, Search, Calculator,
  ArrowUp, ArrowDown, Plus,
} from "lucide-react";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  authLogin, authMe, changePassword, fetchQuotes, fetchStats, updateQuoteStatus,
  addQuoteNote, deleteQuote, createManualLead,
  fetchClients, updateClientStatus, addClientNote, deleteClient, createClient,
} from "@/lib/api";

const STORAGE_KEY = "ai_auth_v2";

const LEAD_STATUS = ["new", "contacted", "quoted", "won", "lost"];
const CLIENT_STATUS = ["active", "renewed", "rewritten", "cancelled", "not_renewed"];
const STATUS_COLOR = {
  new: "bg-terracotta-500 text-forest-900",
  contacted: "bg-forest-500 text-bone-100",
  quoted: "bg-forest-700 text-bone-100",
  won: "bg-bone-100 text-forest-900",
  lost: "bg-forest-800 text-bone-100/70",
  active: "bg-forest-500 text-bone-100",
  renewed: "bg-bone-100 text-forest-900",
  rewritten: "bg-terracotta-500 text-forest-900",
  cancelled: "bg-forest-800 text-bone-100/70",
  not_renewed: "bg-forest-800 text-bone-100/70",
};

// Sort helper — handles strings, numbers, and dates (yyyy-mm-dd or ISO strings).
function sortRows(rows, { by, dir }) {
  const mul = dir === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => {
    const av = a[by];
    const bv = b[by];
    // Empty values always sort last
    if (av == null || av === "") return 1;
    if (bv == null || bv === "") return -1;
    // Numeric
    if (typeof av === "number" && typeof bv === "number") return (av - bv) * mul;
    // Try date parsing (works for ISO strings and yyyy-mm-dd)
    const ad = Date.parse(av);
    const bd = Date.parse(bv);
    if (!Number.isNaN(ad) && !Number.isNaN(bd) && (by.includes("date") || by === "created_at")) {
      return (ad - bd) * mul;
    }
    // String
    return String(av).localeCompare(String(bv), undefined, { sensitivity: "base" }) * mul;
  });
}

const TypeIcon = ({ t }) => {
  if (t === "auto") return <Car size={14} />;
  if (t === "home") return <HomeIcon size={14} />;
  return <Building2 size={14} />;
};

function readAuth() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export default function Admin() {
  const [auth, setAuth] = useState(readAuth());
  const [view, setView] = useState("leads"); // "leads" | "clients" | "calculator"
  const [quotes, setQuotes] = useState([]);
  const [clients, setClients] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [leadFilter, setLeadFilter] = useState("all");
  const [clientFilter, setClientFilter] = useState("all");
  const [selected, setSelected] = useState(null); // {kind: 'lead'|'client', data: {}}
  const [pwOpen, setPwOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [leadSort, setLeadSort] = useState({ by: "created_at", dir: "desc" });
  const [clientSort, setClientSort] = useState({ by: "eff_date", dir: "desc" });
  const [addLeadOpen, setAddLeadOpen] = useState(false);
  const [addClientOpen, setAddClientOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());

  const token = auth?.token;
  const user = auth?.user;
  const isOwner = user?.role === "owner";

  const logout = () => {
    localStorage.removeItem(STORAGE_KEY);
    setAuth(null);
    setQuotes([]);
    setClients([]);
    setStats(null);
    setSelected(null);
  };

  const load = async () => {
    if (!token) return;
    setLoading(true);
    try {
      // verify token still valid
      const me = await authMe(token);
      setAuth((a) => ({ ...a, user: me }));
      const [qs, st, cl] = await Promise.all([
        fetchQuotes(token),
        fetchStats(token),
        fetchClients(token),
      ]);
      setQuotes(qs);
      setStats(st);
      setClients(cl);
    } catch (e) {
      if (e?.response?.status === 401) {
        toast.error("Session expired. Please sign in again.");
        logout();
      } else {
        toast.error("Failed to load data");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  // clear selection when switching tab or filter
  useEffect(() => { setSelectedIds(new Set()); }, [view, leadFilter, clientFilter, search]);

  // ----- keep selected in sync with lists (so notes refresh after add) -----
  useEffect(() => {
    if (!selected) return;
    const list = selected.kind === "lead" ? quotes : clients;
    const fresh = list.find((x) => x.id === selected.data.id);
    if (fresh) setSelected({ kind: selected.kind, data: fresh });
    // eslint-disable-next-line
  }, [quotes, clients]);

  // ---- LEADS ----
  const filteredLeads = useMemo(() => {
    const s = search.toLowerCase();
    const rows = quotes
      .filter((q) => leadFilter === "all" || q.insurance_type === leadFilter)
      .filter((q) =>
        !s ||
        q.full_name?.toLowerCase().includes(s) ||
        q.email?.toLowerCase().includes(s) ||
        q.phone?.toLowerCase().includes(s)
      );
    return sortRows(rows, leadSort);
  }, [quotes, leadFilter, search, leadSort]);

  const setLeadStatus = async (id, s) => {
    try {
      await updateQuoteStatus(id, s, token);
      setQuotes((qs) => qs.map((q) => (q.id === id ? { ...q, status: s } : q)));
      toast.success(`Marked ${s}`);
    } catch {
      toast.error("Failed to update");
    }
  };

  const removeLead = async (id) => {
    if (!window.confirm("Delete this lead? This cannot be undone.")) return;
    try {
      await deleteQuote(id, token);
      setQuotes((qs) => qs.filter((q) => q.id !== id));
      setSelected(null);
      toast.success("Lead deleted");
    } catch {
      toast.error("Failed to delete");
    }
  };

  // ---- CLIENTS (book of business) ----
  const filteredClients = useMemo(() => {
    const s = search.toLowerCase();
    const rows = clients
      .filter((c) => {
        if (clientFilter === "all") return true;
        if (CLIENT_STATUS.includes(clientFilter)) return c.status === clientFilter;
        return c.product?.toLowerCase() === clientFilter;
      })
      .filter((c) =>
        !s ||
        c.name?.toLowerCase().includes(s) ||
        c.email?.toLowerCase().includes(s) ||
        c.phone?.toLowerCase().includes(s) ||
        c.company?.toLowerCase().includes(s)
      );
    return sortRows(rows, clientSort);
  }, [clients, clientFilter, search, clientSort]);

  const setClientStatusLocal = async (id, s) => {
    try {
      await updateClientStatus(id, s, token);
      setClients((cs) => cs.map((c) => (c.id === id ? { ...c, status: s } : c)));
      toast.success(`Marked ${s}`);
    } catch {
      toast.error("Failed to update");
    }
  };

  const removeClient = async (id) => {
    if (!window.confirm("Delete this client record? This cannot be undone.")) return;
    try {
      await deleteClient(id, token);
      setClients((cs) => cs.filter((c) => c.id !== id));
      setSelected(null);
      toast.success("Client deleted");
    } catch {
      toast.error("Failed to delete");
    }
  };

  // ---- BULK ACTIONS ----
  const activeRows = view === "leads" ? filteredLeads : filteredClients;
  const allSelected = activeRows.length > 0 && activeRows.every((r) => selectedIds.has(r.id));
  const someSelected = selectedIds.size > 0 && !allSelected;

  const toggleOne = (id) =>
    setSelectedIds((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  const toggleAll = () =>
    setSelectedIds((s) => {
      if (allSelected) return new Set();
      return new Set(activeRows.map((r) => r.id));
    });

  const bulkSetStatus = async (status) => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    try {
      const fn = view === "leads" ? updateQuoteStatus : updateClientStatus;
      await Promise.all(ids.map((id) => fn(id, status, token)));
      if (view === "leads") setQuotes((qs) => qs.map((q) => (selectedIds.has(q.id) ? { ...q, status } : q)));
      else setClients((cs) => cs.map((c) => (selectedIds.has(c.id) ? { ...c, status } : c)));
      toast.success(`${ids.length} marked ${status.replace("_", " ")}`);
      setSelectedIds(new Set());
    } catch {
      toast.error("Bulk update failed");
    }
  };

  const bulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    if (!window.confirm(`Delete ${ids.length} record(s)? This cannot be undone.`)) return;
    try {
      const fn = view === "leads" ? deleteQuote : deleteClient;
      await Promise.all(ids.map((id) => fn(id, token)));
      if (view === "leads") setQuotes((qs) => qs.filter((q) => !selectedIds.has(q.id)));
      else setClients((cs) => cs.filter((c) => !selectedIds.has(c.id)));
      toast.success(`${ids.length} deleted`);
      setSelectedIds(new Set());
    } catch {
      toast.error("Bulk delete failed");
    }
  };

  const bulkExport = () => {
    const rows = activeRows.filter((r) => selectedIds.has(r.id));
    exportCsv(rows, view);
  };

  // ---- Note submission (shared) ----
  const submitNote = async (text) => {
    if (!selected) return;
    if (!text?.trim()) return;
    try {
      const note = selected.kind === "lead"
        ? await addQuoteNote(selected.data.id, text, token)
        : await addClientNote(selected.data.id, text, token);
      // append optimistically
      const updater = (arr) =>
        arr.map((x) =>
          x.id === selected.data.id
            ? { ...x, notes_log: [...(x.notes_log || []), note] }
            : x
        );
      if (selected.kind === "lead") setQuotes(updater);
      else setClients(updater);
      toast.success("Note added");
    } catch {
      toast.error("Failed to add note");
    }
  };

  // ============ RENDERS ============
  if (!auth) return <LoginScreen onLogin={(a) => { localStorage.setItem(STORAGE_KEY, JSON.stringify(a)); setAuth(a); }} />;

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-forest-900 flex">
      {/* Sidebar */}
      <aside className="w-64 bg-forest-800 border-r border-bone-100/10 hidden lg:flex flex-col p-6">
        <div className="font-serif text-2xl text-bone-100 tracking-tight">
          Always<span className="italic text-terracotta-500">Insured</span>
        </div>
        <div className="overline text-bone-100/50 mt-1 mb-8">Broker console</div>

        <div className="mb-6">
          <div className="text-sm text-bone-100">{user?.name}</div>
          <div className="text-xs text-bone-100/50 uppercase tracking-wider mt-0.5">
            {user?.role} · {user?.email}
          </div>
        </div>

        <nav className="space-y-1 mb-8">
          <button
            data-testid="nav-leads"
            onClick={() => setView("leads")}
            className={`w-full flex items-center gap-2 text-left px-3 py-2 text-sm transition-colors ${
              view === "leads" ? "bg-terracotta-500 text-forest-900 font-medium" : "text-bone-100 hover:bg-bone-100/10"
            }`}
          >
            <Inbox size={14} /> New Leads
            <span className="ml-auto opacity-70">{quotes.length}</span>
          </button>
          <button
            data-testid="nav-clients"
            onClick={() => setView("clients")}
            className={`w-full flex items-center gap-2 text-left px-3 py-2 text-sm transition-colors ${
              view === "clients" ? "bg-terracotta-500 text-forest-900 font-medium" : "text-bone-100 hover:bg-bone-100/10"
            }`}
          >
            <Users size={14} /> Book of Business
            <span className="ml-auto opacity-70">{clients.length}</span>
          </button>
          <button
            data-testid="nav-calculator"
            onClick={() => setView("calculator")}
            className={`w-full flex items-center gap-2 text-left px-3 py-2 text-sm transition-colors ${
              view === "calculator" ? "bg-terracotta-500 text-forest-900 font-medium" : "text-bone-100 hover:bg-bone-100/10"
            }`}
          >
            <Calculator size={14} /> Premium calculator
          </button>
        </nav>

        {view === "leads" && (
          <div className="space-y-1 mb-8">
            <div className="overline text-bone-100/40 mt-2 mb-2 px-3">Filter · Type</div>
            {[
              { k: "all", l: "All" },
              { k: "auto", l: "Auto" },
              { k: "home", l: "Home" },
              { k: "commercial", l: "Commercial" },
            ].map((n) => (
              <button
                key={n.k}
                data-testid={`lead-filter-${n.k}`}
                onClick={() => setLeadFilter(n.k)}
                className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                  leadFilter === n.k ? "bg-bone-100/10 text-bone-100 font-medium" : "text-bone-100/70 hover:bg-bone-100/5"
                }`}
              >
                {n.l}
                <span className="float-right opacity-70">
                  {n.k === "all" ? quotes.length : quotes.filter((q) => q.insurance_type === n.k).length}
                </span>
              </button>
            ))}
          </div>
        )}

        {view === "clients" && (
          <div className="space-y-1 mb-8">
            <div className="overline text-bone-100/40 mt-2 mb-2 px-3">Filter · Status</div>
            {[{ k: "all", l: "All" }, ...CLIENT_STATUS.map((s) => ({ k: s, l: s.replace("_", " ") }))].map((n) => (
              <button
                key={n.k}
                data-testid={`client-filter-${n.k}`}
                onClick={() => setClientFilter(n.k)}
                className={`w-full text-left px-3 py-2 text-sm capitalize transition-colors ${
                  clientFilter === n.k ? "bg-bone-100/10 text-bone-100 font-medium" : "text-bone-100/70 hover:bg-bone-100/5"
                }`}
              >
                {n.l}
                <span className="float-right opacity-70">
                  {n.k === "all" ? clients.length : clients.filter((c) => c.status === n.k).length}
                </span>
              </button>
            ))}
          </div>
        )}

        <div className="mt-auto space-y-2">
          <button
            data-testid="admin-refresh"
            onClick={load}
            className="w-full flex items-center gap-2 text-sm text-bone-100 hover:text-bone-100 py-2"
          >
            <RefreshCcw size={14} /> Refresh
          </button>
          <button
            data-testid="admin-change-pw"
            onClick={() => setPwOpen(true)}
            className="w-full flex items-center gap-2 text-sm text-bone-100 hover:text-bone-100 py-2"
          >
            <KeyRound size={14} /> Change password
          </button>
          <button
            data-testid="admin-logout"
            onClick={logout}
            className="w-full flex items-center gap-2 text-sm text-bone-100 hover:text-bone-100 py-2"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 p-6 md:p-10 max-w-full overflow-x-hidden">
        <div className="flex items-center justify-between mb-8 gap-4 flex-wrap">
          <div>
            <h1 className="font-serif text-3xl md:text-4xl text-bone-100 tracking-tight">
              {view === "leads" ? "Quote requests" : view === "clients" ? "Book of business" : "Premium calculator"}
            </h1>
            <div className="overline text-terracotta-500 mt-2">
              {view === "leads" ? "New leads · from website" : view === "clients" ? "Clients · 2025 imports & renewals" : "Auto + Home · monthly & downpayment"}
            </div>
          </div>
          {view !== "calculator" && (
            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-bone-100/40" />
                <Input
                  data-testid="admin-search"
                  placeholder="Search name / email / phone"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9 w-64 bg-forest-800 border-bone-100/15 text-bone-100"
                />
              </div>
              <button
                data-testid={view === "leads" ? "add-lead-btn" : "add-client-btn"}
                onClick={() => (view === "leads" ? setAddLeadOpen(true) : setAddClientOpen(true))}
                className="inline-flex items-center gap-2 border border-terracotta-500 text-terracotta-500 hover:bg-terracotta-500 hover:text-forest-900 transition-colors px-4 py-2 text-sm font-medium"
              >
                <Plus size={14} /> {view === "leads" ? "Add lead" : "Add client"}
              </button>
              <button
                data-testid="admin-export-csv"
                onClick={() => exportCsv(view === "leads" ? filteredLeads : filteredClients, view)}
                className="inline-flex items-center gap-2 bg-terracotta-500 text-forest-900 hover:bg-terracotta-400 transition-colors px-4 py-2 text-sm font-medium"
              >
                <Download size={14} /> Export CSV
              </button>
            </div>
          )}
        </div>

        {view === "calculator" && <CalculatorView />}

        {view !== "calculator" && (
          <>
        {/* Bulk actions bar */}
        {selectedIds.size > 0 && (
          <div data-testid="bulk-bar" className="mb-6 bg-terracotta-500 text-forest-900 px-5 py-3 flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <span className="font-serif text-xl">{selectedIds.size}</span>
              <span className="text-sm">selected</span>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="bulk-status-btn" className="bg-forest-900 text-bone-100 px-3 py-1.5 text-xs uppercase tracking-wider hover:bg-forest-800">
                    Set status ▾
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuLabel>Bulk set status</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {(view === "leads" ? LEAD_STATUS : CLIENT_STATUS).map((s) => (
                    <DropdownMenuItem key={s} onClick={() => bulkSetStatus(s)}>
                      {s.replace("_", " ")}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              <button
                data-testid="bulk-export-btn"
                onClick={bulkExport}
                className="bg-forest-900 text-bone-100 px-3 py-1.5 text-xs uppercase tracking-wider hover:bg-forest-800 inline-flex items-center gap-1.5"
              >
                <Download size={12} /> Export
              </button>
              {isOwner && (
                <button
                  data-testid="bulk-delete-btn"
                  onClick={bulkDelete}
                  className="bg-forest-900 text-red-300 px-3 py-1.5 text-xs uppercase tracking-wider hover:bg-red-900/40 inline-flex items-center gap-1.5"
                >
                  <Trash2 size={12} /> Delete
                </button>
              )}
              <button
                data-testid="bulk-clear-btn"
                onClick={() => setSelectedIds(new Set())}
                className="text-xs uppercase tracking-wider text-forest-900/70 hover:text-forest-900 px-2"
              >
                Clear
              </button>
            </div>
          </div>
        )}

        {/* Stats — only for leads view */}
        {view === "leads" && stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            <StatCard label="Total" value={stats.total} />
            <StatCard label="New" value={stats.new} accent />
            <StatCard label="Auto" value={stats.by_type.auto} />
            <StatCard label="Home" value={stats.by_type.home} />
            <StatCard label="Commercial" value={stats.by_type.commercial} />
          </div>
        )}

        {/* Book of Business summary */}
        {view === "clients" && (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-8">
            <StatCard label="Total" value={clients.length} />
            {CLIENT_STATUS.map((s) => (
              <StatCard key={s} label={s.replace("_", " ")} value={clients.filter((c) => c.status === s).length} />
            ))}
          </div>
        )}

        {/* Table */}
        <div className="bg-forest-800 border border-bone-100/10" data-testid="admin-table">
          <Table>
            <TableHeader>
              <TableRow className="bg-forest-700 hover:bg-forest-700 border-b border-bone-100/10">
                <TableHead className="w-10">
                  <input
                    data-testid="select-all"
                    type="checkbox"
                    checked={allSelected}
                    ref={(el) => el && (el.indeterminate = someSelected)}
                    onChange={toggleAll}
                    className="accent-terracotta-500 w-4 h-4 cursor-pointer"
                  />
                </TableHead>
                {view === "leads" ? (
                  <>
                    <SortHead label="Name" col="full_name" sort={leadSort} setSort={setLeadSort} />
                    <SortHead label="Type" col="insurance_type" sort={leadSort} setSort={setLeadSort} />
                    <TableHead className="text-bone-100 hidden md:table-cell">Contact</TableHead>
                    <SortHead label="Received" col="created_at" sort={leadSort} setSort={setLeadSort} className="hidden lg:table-cell" />
                    <SortHead label="Status" col="status" sort={leadSort} setSort={setLeadSort} />
                    <TableHead className="text-bone-100"></TableHead>
                  </>
                ) : (
                  <>
                    <SortHead label="Name" col="name" sort={clientSort} setSort={setClientSort} />
                    <SortHead label="Product" col="product" sort={clientSort} setSort={setClientSort} />
                    <SortHead label="Carrier" col="company" sort={clientSort} setSort={setClientSort} className="hidden md:table-cell" />
                    <SortHead label="Premium" col="premium" sort={clientSort} setSort={setClientSort} className="hidden md:table-cell" />
                    <SortHead label="Eff. date" col="eff_date" sort={clientSort} setSort={setClientSort} className="hidden lg:table-cell" />
                    <SortHead label="Status" col="status" sort={clientSort} setSort={setClientSort} />
                    <TableHead className="text-bone-100"></TableHead>
                  </>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {view === "leads" ? (
                filteredLeads.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-16 text-bone-100/50">
                      {loading ? "Loading..." : "No leads yet."}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredLeads.map((q) => (
                    <TableRow key={q.id} data-testid={`lead-row-${q.id}`} className={`hover:bg-bone-100/5 border-b border-bone-100/5 ${selectedIds.has(q.id) ? "bg-terracotta-500/10" : ""}`}>
                      <TableCell>
                        <input
                          data-testid={`select-lead-${q.id}`}
                          type="checkbox"
                          checked={selectedIds.has(q.id)}
                          onChange={() => toggleOne(q.id)}
                          onClick={(e) => e.stopPropagation()}
                          className="accent-terracotta-500 w-4 h-4 cursor-pointer"
                        />
                      </TableCell>
                      <TableCell className="font-medium text-bone-100">{q.full_name}</TableCell>
                      <TableCell>
                        <span className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider text-bone-100/70">
                          <TypeIcon t={q.insurance_type} /> {q.insurance_type}
                        </span>
                      </TableCell>
                      <TableCell className="hidden md:table-cell text-sm text-bone-100/70">
                        <div className="flex items-center gap-2"><Mail size={12} /> {q.email}</div>
                        <div className="flex items-center gap-2 mt-1"><PhoneIcon size={12} /> {q.phone}</div>
                      </TableCell>
                      <TableCell className="hidden lg:table-cell text-sm text-bone-100/70">
                        {new Date(q.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <StatusDropdown current={q.status} options={LEAD_STATUS} onSelect={(s) => setLeadStatus(q.id, s)} testid={`lead-status-${q.id}`} />
                      </TableCell>
                      <TableCell className="text-right">
                        <button
                          data-testid={`lead-view-${q.id}`}
                          onClick={() => setSelected({ kind: "lead", data: q })}
                          className="text-sm text-bone-100 hover:text-terracotta-600 underline underline-offset-4"
                        >
                          View
                        </button>
                      </TableCell>
                    </TableRow>
                  ))
                )
              ) : filteredClients.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-16 text-bone-100/50">
                    {loading ? "Loading..." : "No clients yet."}
                  </TableCell>
                </TableRow>
              ) : (
                filteredClients.map((c) => (
                  <TableRow key={c.id} data-testid={`client-row-${c.id}`} className={`hover:bg-bone-100/5 border-b border-bone-100/5 ${selectedIds.has(c.id) ? "bg-terracotta-500/10" : ""}`}>
                    <TableCell>
                      <input
                        data-testid={`select-client-${c.id}`}
                        type="checkbox"
                        checked={selectedIds.has(c.id)}
                        onChange={() => toggleOne(c.id)}
                        onClick={(e) => e.stopPropagation()}
                        className="accent-terracotta-500 w-4 h-4 cursor-pointer"
                      />
                    </TableCell>
                    <TableCell className="font-medium text-bone-100">{c.name}</TableCell>
                    <TableCell className="text-sm text-bone-100/80 uppercase tracking-wider">{c.product}</TableCell>
                    <TableCell className="hidden md:table-cell text-sm text-bone-100/70 uppercase">{c.company}</TableCell>
                    <TableCell className="hidden md:table-cell text-sm text-bone-100/70">
                      {c.premium ? `$${Number(c.premium).toLocaleString()}` : "—"}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell text-sm text-bone-100/70">{c.eff_date || "—"}</TableCell>
                    <TableCell>
                      <StatusDropdown current={c.status} options={CLIENT_STATUS} onSelect={(s) => setClientStatusLocal(c.id, s)} testid={`client-status-${c.id}`} />
                    </TableCell>
                    <TableCell className="text-right">
                      <button
                        data-testid={`client-view-${c.id}`}
                        onClick={() => setSelected({ kind: "client", data: c })}
                        className="text-sm text-bone-100 hover:text-terracotta-600 underline underline-offset-4"
                      >
                        View
                      </button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
          </>
        )}
      </main>

      {/* Detail drawer */}
      {selected && (
        <DetailDrawer
          selected={selected}
          onClose={() => setSelected(null)}
          onAddNote={submitNote}
          onDelete={
            isOwner
              ? () => (selected.kind === "lead" ? removeLead(selected.data.id) : removeClient(selected.data.id))
              : null
          }
        />
      )}

      {pwOpen && (
        <ChangePasswordModal
          token={token}
          onClose={() => setPwOpen(false)}
          onDone={() => { setPwOpen(false); toast.success("Password updated"); }}
        />
      )}

      {addLeadOpen && (
        <AddLeadModal
          token={token}
          onClose={() => setAddLeadOpen(false)}
          onCreated={(lead) => {
            setQuotes((qs) => [lead, ...qs]);
            setAddLeadOpen(false);
            toast.success("Lead added");
          }}
        />
      )}

      {addClientOpen && (
        <AddClientModal
          token={token}
          onClose={() => setAddClientOpen(false)}
          onCreated={(c) => {
            setClients((cs) => [c, ...cs]);
            setAddClientOpen(false);
            toast.success("Client added");
          }}
        />
      )}
    </div>
  );
}

// ============ LOGIN SCREEN ============
function LoginScreen({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!email || !password) return toast.error("Enter email and password");
    setBusy(true);
    try {
      const res = await authLogin(email.trim().toLowerCase(), password);
      onLogin({ token: res.token, user: res.user });
      toast.success(`Welcome, ${res.user.name}`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Sign in failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-login" className="min-h-screen bg-forest-900 flex items-center justify-center px-6">
      <div className="w-full max-w-sm">
        <div className="font-serif text-3xl text-bone-100 tracking-tight">
          Always<span className="italic text-terracotta-500">Insured</span>
        </div>
        <div className="overline text-bone-100/50 mt-2 mb-10">Broker sign in</div>
        <label className="block text-xs uppercase tracking-wider text-bone-100/70 mb-2">Email</label>
        <Input
          data-testid="login-email"
          type="email"
          placeholder="you@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && submit()}
          className="mb-4"
        />
        <label className="block text-xs uppercase tracking-wider text-bone-100/70 mb-2">Password</label>
        <Input
          data-testid="login-password"
          type="password"
          placeholder="••••••••"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && submit()}
          className="mb-6"
        />
        <button
          data-testid="login-submit"
          onClick={submit}
          disabled={busy}
          className="w-full bg-terracotta-500 text-forest-900 py-3 hover:bg-terracotta-400 disabled:opacity-40 transition-colors font-medium"
        >
          {busy ? "Signing in..." : "Sign in"}
        </button>
      </div>
    </div>
  );
}

// ============ CHANGE PASSWORD ============
function ChangePasswordModal({ token, onClose, onDone }) {
  const [curr, setCurr] = useState("");
  const [next, setNext] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!curr || !next) return toast.error("Fill both fields");
    if (next.length < 8) return toast.error("New password must be at least 8 characters");
    setBusy(true);
    try {
      await changePassword(curr, next, token);
      onDone();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to change password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-6" onClick={onClose}>
      <div className="bg-forest-800 border border-bone-100/15 p-8 w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
        <div className="font-serif text-2xl text-bone-100 mb-1">Change password</div>
        <div className="overline text-bone-100/50 mb-6">Min 8 characters</div>
        <label className="block text-xs uppercase tracking-wider text-bone-100/70 mb-2">Current</label>
        <Input type="password" data-testid="pw-current" value={curr} onChange={(e) => setCurr(e.target.value)} className="mb-4" />
        <label className="block text-xs uppercase tracking-wider text-bone-100/70 mb-2">New</label>
        <Input type="password" data-testid="pw-new" value={next} onChange={(e) => setNext(e.target.value)} className="mb-6" />
        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 border border-bone-100/20 text-bone-100 py-2 hover:bg-bone-100/5">Cancel</button>
          <button
            data-testid="pw-submit"
            onClick={submit}
            disabled={busy}
            className="flex-1 bg-terracotta-500 text-forest-900 py-2 hover:bg-terracotta-400 disabled:opacity-40 font-medium"
          >
            {busy ? "Saving..." : "Update"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============ DETAIL DRAWER ============
function DetailDrawer({ selected, onClose, onAddNote, onDelete }) {
  const [noteText, setNoteText] = useState("");
  const [posting, setPosting] = useState(false);
  const item = selected.data;
  const isLead = selected.kind === "lead";

  const handleSubmit = async () => {
    if (!noteText.trim()) return;
    setPosting(true);
    await onAddNote(noteText);
    setNoteText("");
    setPosting(false);
  };

  const notes = item.notes_log || [];

  return (
    <div
      data-testid="detail-overlay"
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex justify-end"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md bg-forest-800 h-full overflow-y-auto p-8 border-l border-bone-100/15"
        onClick={(e) => e.stopPropagation()}
        data-testid="detail-panel"
      >
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="font-serif text-2xl text-bone-100">{isLead ? item.full_name : item.name}</div>
            <div className="overline text-terracotta-600 mt-2">
              {isLead ? "Lead detail" : "Client detail"}
            </div>
          </div>
          <button data-testid="detail-close" onClick={onClose} className="text-bone-100/70 hover:text-bone-100">Close</button>
        </div>

        <dl className="space-y-3 text-sm mb-8">
          {isLead ? (
            <>
              <Row label="Type">{item.insurance_type}</Row>
              <Row label="Email">{item.email || "—"}</Row>
              <Row label="Phone">{item.phone || "—"}</Row>
              {item.insurance_type === "auto" && (
                <>
                  <Row label="Driver's license">{item.license_number || "—"}</Row>
                  <Row label="VIN">{item.vin || "—"}</Row>
                  <Row label="Coverage">
                    {item.coverage_type === "full" ? "Full" : item.coverage_type === "third_party" ? "Third-party" : "—"}
                  </Row>
                </>
              )}
              {item.insurance_type === "home" && <Row label="Property">{item.property_type || "—"}</Row>}
              {item.insurance_type === "commercial" && (
                <>
                  <Row label="Business">{item.business_name || "—"}</Row>
                  <Row label="Industry">{item.business_type || "—"}</Row>
                </>
              )}
              {item.quote_stage && <Row label="Urgency">{item.quote_stage === "new" ? "New purchase" : "Renewal"}</Row>}
              {item.stage_date && (
                <Row label={item.quote_stage === "renewal" ? "Renewal date" : "Target date"}>{item.stage_date}</Row>
              )}
              <Row label="Notes (from form)">{item.coverage_notes || "—"}</Row>
              <Row label="Received">{new Date(item.created_at).toLocaleString()}</Row>
            </>
          ) : (
            <>
              <Row label="Product">{item.product || "—"}</Row>
              <Row label="Carrier">{item.company || "—"}</Row>
              <Row label="Premium">{item.premium ? `$${Number(item.premium).toLocaleString()}` : "—"}</Row>
              <Row label="Email">{item.email || "—"}</Row>
              <Row label="Phone">{item.phone || "—"}</Row>
              <Row label="Eff. date">{item.eff_date || "—"}</Row>
              <Row label="Source month">{item.source_month || "—"}</Row>
              {item.referred_by && <Row label="Referred by">{item.referred_by}</Row>}
              <Row label="Status">{(item.status || "").replace("_", " ") || "—"}</Row>
              {item.original_notes && <Row label="Legacy note">{item.original_notes}</Row>}
            </>
          )}
        </dl>

        {/* Notes log */}
        <div className="border-t border-bone-100/10 pt-6">
          <div className="flex items-center gap-2 mb-4">
            <MessageSquare size={14} className="text-terracotta-500" />
            <div className="overline text-bone-100/70">Remarks · Notes ({notes.length})</div>
          </div>

          <div className="space-y-3 mb-5 max-h-64 overflow-y-auto">
            {notes.length === 0 && <div className="text-sm text-bone-100/50 italic">No notes yet — add the first one.</div>}
            {notes.slice().reverse().map((n) => (
              <div key={n.id} className="bg-forest-900 border border-bone-100/10 p-3" data-testid={`note-${n.id}`}>
                <div className="text-sm text-bone-100 leading-relaxed whitespace-pre-wrap">{n.text}</div>
                <div className="text-xs text-bone-100/50 mt-2">
                  <span className="text-terracotta-400 font-medium">{n.author_name}</span>
                  {" · "}
                  {new Date(n.created_at).toLocaleString()}
                </div>
              </div>
            ))}
          </div>

          <Textarea
            data-testid="note-input"
            rows={3}
            placeholder="Add a note — carrier update, follow-up, quote comparison…"
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            className="mb-3 bg-forest-900 border-bone-100/15 text-bone-100"
          />
          <button
            data-testid="note-submit"
            disabled={posting || !noteText.trim()}
            onClick={handleSubmit}
            className="w-full bg-terracotta-500 text-forest-900 hover:bg-terracotta-400 transition-colors py-2.5 disabled:opacity-40 font-medium"
          >
            {posting ? "Adding..." : "Add note"}
          </button>
        </div>

        <div className="mt-8 flex flex-col gap-3">
          {isLead && (
            <a
              href={`mailto:${item.email}?subject=Your AlwaysInsured quote`}
              className="inline-flex items-center justify-center gap-2 bg-terracotta-500 text-forest-900 px-5 py-3 hover:bg-terracotta-400 font-medium"
              data-testid="reply-email"
            >
              <Mail size={14} /> Reply by email
            </a>
          )}
          {onDelete && (
            <button
              data-testid="detail-delete"
              onClick={onDelete}
              className="inline-flex items-center justify-center gap-2 border border-red-500/40 text-red-300 hover:bg-red-500/10 px-5 py-2.5 text-sm"
            >
              <Trash2 size={14} /> Delete (owner only)
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ============ SMALL BITS ============
const StatCard = ({ label, value, accent }) => (
  <div className={`border p-5 ${accent ? "bg-terracotta-500 text-forest-900 border-terracotta-500" : "bg-forest-800 border-bone-100/10"}`}>
    <div className={`font-serif text-3xl md:text-4xl ${accent ? "text-forest-900" : "text-bone-100"}`}>{value}</div>
    <div className={`overline mt-2 capitalize ${accent ? "text-forest-900/70" : "text-bone-100/50"}`}>{label}</div>
  </div>
);

const Row = ({ label, children }) => (
  <div className="flex justify-between gap-6 border-b border-bone-100/10 pb-2">
    <dt className="overline text-bone-100/50">{label}</dt>
    <dd className="text-bone-100 text-right break-words">{children}</dd>
  </div>
);

const SortHead = ({ label, col, sort, setSort, className = "" }) => {
  const active = sort.by === col;
  const dir = active ? sort.dir : null;
  const onClick = () => {
    if (!active) return setSort({ by: col, dir: "asc" });
    setSort({ by: col, dir: sort.dir === "asc" ? "desc" : "asc" });
  };
  return (
    <TableHead className={`text-bone-100 ${className}`}>
      <button
        type="button"
        onClick={onClick}
        data-testid={`sort-${col}`}
        className={`inline-flex items-center gap-1.5 hover:text-terracotta-400 transition-colors ${
          active ? "text-terracotta-400" : "text-bone-100"
        }`}
      >
        {label}
        {dir === "asc" && <ArrowUp size={12} />}
        {dir === "desc" && <ArrowDown size={12} />}
      </button>
    </TableHead>
  );
};

const StatusDropdown = ({ current, options, onSelect, testid }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <button
        data-testid={testid}
        className={`text-xs uppercase tracking-wider px-2.5 py-1 ${STATUS_COLOR[current] || "bg-charcoal-800 text-bone-100"}`}
      >
        {(current || "—").replace("_", " ")}
      </button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="start">
      <DropdownMenuLabel>Set status</DropdownMenuLabel>
      <DropdownMenuSeparator />
      {options.map((s) => (
        <DropdownMenuItem key={s} onClick={() => onSelect(s)}>
          {s.replace("_", " ")}
        </DropdownMenuItem>
      ))}
    </DropdownMenuContent>
  </DropdownMenu>
);

// ============ CSV EXPORT ============
function exportCsv(rows, kind) {
  if (!rows.length) return toast.error("Nothing to export");
  const csvEscape = (v) => {
    if (v === null || v === undefined) return "";
    const s = String(v).replace(/"/g, '""');
    return /[",\n]/.test(s) ? `"${s}"` : s;
  };
  const stageLabel = (s) => (s === "new" ? "New purchase" : s === "renewal" ? "Renewal" : "");

  let headers, mapper;
  if (kind === "leads") {
    headers = ["Received", "Status", "Type", "Full name", "Email", "Phone", "License", "VIN", "Coverage",
      "Property type", "Business name", "Industry", "Urgency", "Urgency date", "Notes", "Quote ID"];
    mapper = (q) => [
      new Date(q.created_at).toISOString(), q.status, q.insurance_type, q.full_name, q.email, q.phone,
      q.license_number || "", q.vin || "",
      q.coverage_type === "full" ? "Full" : q.coverage_type === "third_party" ? "Third-party" : "",
      q.property_type || "", q.business_name || "", q.business_type || "",
      stageLabel(q.quote_stage), q.stage_date || "", q.coverage_notes || "", q.id,
    ];
  } else {
    headers = ["Eff. date", "Name", "Product", "Carrier", "Premium", "Email", "Phone", "Status", "Source month", "Referred by", "Legacy note"];
    mapper = (c) => [
      c.eff_date || "", c.name, c.product, c.company, c.premium || "", c.email || "", c.phone || "",
      (c.status || "").replace("_", " "), c.source_month || "", c.referred_by || "", c.original_notes || "",
    ];
  }
  const body = rows.map((r) => mapper(r).map(csvEscape).join(","));
  const csv = [headers.join(","), ...body].join("\n");
  const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  const date = new Date().toISOString().slice(0, 10);
  a.href = url;
  a.download = `alwaysinsured-${kind}-${date}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
  toast.success(`Exported ${rows.length}`);
}


// ============ CALCULATOR ============
// Ontario-specific broker calc mirroring the Excel TOOLS tab.
// Auto: no tax; finance interest 1.3%. Home: 8% PST + 3% interest on (premium+tax).
// 12-month term, 2-month downpayment.
function CalculatorView() {
  const [autoPrem, setAutoPrem] = useState("");
  const [homePrem, setHomePrem] = useState("");
  const [autoInt, setAutoInt] = useState(1.3);
  const [homeTax, setHomeTax] = useState(8);
  const [homeInt, setHomeInt] = useState(3);
  const [term, setTerm] = useState(12);
  const [dpMonths, setDpMonths] = useState(2);

  const fmt = (n) => (Number.isFinite(n) ? `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "—");

  const a = Number(autoPrem) || 0;
  const h = Number(homePrem) || 0;

  // AUTO
  const aTax = 0;
  const aInterest = a * (autoInt / 100);
  const aTotal = a + aInterest;
  const aMonthly = aTotal / term;
  const aDown = aMonthly * dpMonths;

  // HOME
  const hTax = h * (homeTax / 100);
  const hInterestBase = h + hTax;
  const hInterest = hInterestBase * (homeInt / 100);
  const hTotal = h + hTax + hInterest;
  const hMonthly = hTotal / term;
  const hDown = hMonthly * dpMonths;

  const bundleMonthly = aMonthly + hMonthly;
  const bundleDown = aDown + hDown;

  const reset = () => {
    setAutoPrem("");
    setHomePrem("");
  };

  return (
    <div data-testid="calculator-view" className="max-w-5xl">
      {/* Rate settings */}
      <details className="mb-8 bg-forest-800 border border-bone-100/10 p-4">
        <summary className="cursor-pointer text-sm text-bone-100/70 hover:text-bone-100 flex items-center gap-2">
          <KeyRound size={14} /> Advanced rates (edit if the carrier changes finance terms)
        </summary>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-4">
          <RateField label="Auto interest %" value={autoInt} onChange={setAutoInt} testid="rate-auto-int" />
          <RateField label="Home tax %" value={homeTax} onChange={setHomeTax} testid="rate-home-tax" />
          <RateField label="Home interest %" value={homeInt} onChange={setHomeInt} testid="rate-home-int" />
          <RateField label="Term (months)" value={term} onChange={setTerm} testid="rate-term" step={1} />
          <RateField label="Downpayment (months)" value={dpMonths} onChange={setDpMonths} testid="rate-dp" step={1} />
        </div>
      </details>

      {/* Auto card */}
      <CalcCard
        title="Auto"
        icon={<Car size={18} />}
        premium={autoPrem}
        onPremium={setAutoPrem}
        rows={[
          ["Tax", aTax > 0 ? fmt(aTax) : "—"],
          [`Interest (${autoInt}%)`, fmt(aInterest)],
          ["Total", fmt(aTotal)],
          [`Monthly (÷${term})`, fmt(aMonthly)],
          [`Downpayment (${dpMonths} mo)`, fmt(aDown), true],
        ]}
        testid="calc-auto"
      />

      {/* Home card */}
      <CalcCard
        title="Home"
        icon={<HomeIcon size={18} />}
        premium={homePrem}
        onPremium={setHomePrem}
        rows={[
          [`Tax (${homeTax}%)`, fmt(hTax)],
          [`Interest (${homeInt}% of premium + tax)`, fmt(hInterest)],
          ["Total", fmt(hTotal)],
          [`Monthly (÷${term})`, fmt(hMonthly)],
          [`Downpayment (${dpMonths} mo)`, fmt(hDown), true],
        ]}
        testid="calc-home"
      />

      {/* Combined summary */}
      {(a > 0 || h > 0) && (
        <div className="bg-terracotta-500 text-forest-900 p-6 border border-terracotta-500 flex flex-col md:flex-row items-start md:items-center justify-between gap-4" data-testid="calc-summary">
          <div>
            <div className="font-serif text-3xl">Bundle summary</div>
            <div className="overline text-forest-900/60 mt-1">Auto + Home combined</div>
          </div>
          <div className="grid grid-cols-2 gap-8">
            <div>
              <div className="font-serif text-3xl md:text-4xl" data-testid="calc-bundle-monthly">{fmt(bundleMonthly)}</div>
              <div className="overline text-forest-900/70 mt-1">Total monthly</div>
            </div>
            <div>
              <div className="font-serif text-3xl md:text-4xl" data-testid="calc-bundle-down">{fmt(bundleDown)}</div>
              <div className="overline text-forest-900/70 mt-1">Total downpayment</div>
            </div>
          </div>
        </div>
      )}

      <div className="mt-8 flex gap-3">
        <button
          data-testid="calc-reset"
          onClick={reset}
          className="border border-bone-100/20 text-bone-100 px-5 py-2 text-sm hover:bg-bone-100/5"
        >
          Reset
        </button>
        <div className="text-xs text-bone-100/40 flex items-center gap-2">
          Ontario defaults · adjust rates above per carrier if needed.
        </div>
      </div>
    </div>
  );
}

const RateField = ({ label, value, onChange, testid, step = 0.1 }) => (
  <div>
    <label className="block text-xs uppercase tracking-wider text-bone-100/50 mb-2">{label}</label>
    <Input
      data-testid={testid}
      type="number"
      step={step}
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      className="bg-forest-900 border-bone-100/15 text-bone-100"
    />
  </div>
);

const CalcCard = ({ title, icon, premium, onPremium, rows, testid }) => (
  <div className="bg-forest-800 border border-bone-100/10 p-6 mb-6" data-testid={testid}>
    <div className="flex items-center gap-3 mb-6">
      <span className="text-terracotta-500">{icon}</span>
      <div>
        <div className="font-serif text-2xl text-bone-100">{title}</div>
        <div className="overline text-bone-100/50 mt-1">Enter net premium</div>
      </div>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
      <div>
        <label className="block text-xs uppercase tracking-wider text-bone-100/70 mb-2">Premium ($)</label>
        <Input
          data-testid={`${testid}-premium`}
          type="number"
          value={premium}
          onChange={(e) => onPremium(e.target.value)}
          placeholder="e.g. 9433"
          className="bg-forest-900 border-bone-100/15 text-bone-100 text-lg"
        />
      </div>
      <div className="md:col-span-2 space-y-2">
        {rows.map(([label, value, emphasize]) => (
          <div
            key={label}
            className={`flex justify-between items-baseline border-b border-bone-100/10 pb-2 ${
              emphasize ? "bg-forest-900 -mx-2 px-2 py-2 border-terracotta-500/50" : ""
            }`}
          >
            <span className={`text-sm ${emphasize ? "text-terracotta-400 uppercase tracking-wider font-medium" : "text-bone-100/60"}`}>
              {label}
            </span>
            <span className={`font-serif ${emphasize ? "text-terracotta-400 text-2xl" : "text-bone-100 text-lg"}`}>
              {value}
            </span>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// ============ ADD LEAD MODAL ============
function AddLeadModal({ token, onClose, onCreated }) {
  const [f, setF] = useState({
    insurance_type: "auto",
    full_name: "",
    email: "",
    phone: "",
    coverage_type: "",
    property_type: "",
    business_name: "",
    business_type: "",
    quote_stage: "",
    stage_date: "",
    coverage_notes: "",
    license_number: "",
    vin: "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target?.value ?? e });

  const submit = async () => {
    if (!f.full_name || !f.email || !f.phone) return toast.error("Name, email and phone are required");
    setBusy(true);
    try {
      const lead = await createManualLead(f, token);
      onCreated(lead);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed to add lead");
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell title="Add lead manually" subtitle="Walk-in · phone · referral" onClose={onClose}>
      <FieldRow>
        <Segment
          label="Insurance type"
          value={f.insurance_type}
          onChange={(v) => setF({ ...f, insurance_type: v })}
          options={[
            { k: "auto", l: "Auto" },
            { k: "home", l: "Home" },
            { k: "commercial", l: "Commercial" },
          ]}
        />
      </FieldRow>
      <FieldRow cols={2}>
        <FInput label="Full name *" value={f.full_name} onChange={set("full_name")} testid="add-name" />
        <FInput label="Phone *" value={f.phone} onChange={set("phone")} testid="add-phone" />
      </FieldRow>
      <FieldRow>
        <FInput label="Email *" type="email" value={f.email} onChange={set("email")} testid="add-email" />
      </FieldRow>

      {f.insurance_type === "auto" && (
        <>
          <FieldRow cols={2}>
            <FInput label="Driver's license" value={f.license_number} onChange={set("license_number")} />
            <FInput label="VIN" value={f.vin} onChange={set("vin")} />
          </FieldRow>
          <FieldRow>
            <Segment
              label="Coverage"
              value={f.coverage_type}
              onChange={(v) => setF({ ...f, coverage_type: v })}
              options={[
                { k: "full", l: "Full" },
                { k: "third_party", l: "Third-party" },
              ]}
            />
          </FieldRow>
        </>
      )}
      {f.insurance_type === "home" && (
        <FieldRow>
          <FInput label="Property type" value={f.property_type} onChange={set("property_type")} />
        </FieldRow>
      )}
      {f.insurance_type === "commercial" && (
        <FieldRow cols={2}>
          <FInput label="Business name" value={f.business_name} onChange={set("business_name")} />
          <FInput label="Industry" value={f.business_type} onChange={set("business_type")} />
        </FieldRow>
      )}

      <FieldRow cols={2}>
        <Segment
          label="Urgency"
          value={f.quote_stage}
          onChange={(v) => setF({ ...f, quote_stage: v })}
          options={[
            { k: "new", l: "New purchase" },
            { k: "renewal", l: "Renewal" },
          ]}
        />
        <FInput label={f.quote_stage === "renewal" ? "Renewal date" : "Target date"} type="date" value={f.stage_date} onChange={set("stage_date")} />
      </FieldRow>
      <FieldRow>
        <label className="block text-xs uppercase tracking-wider text-bone-100/70 mb-2">Notes</label>
        <Textarea rows={2} value={f.coverage_notes} onChange={set("coverage_notes")} className="bg-forest-900 border-bone-100/15 text-bone-100" />
      </FieldRow>

      <ModalActions busy={busy} onClose={onClose} onSubmit={submit} testid="add-lead-submit" label="Add lead" />
    </ModalShell>
  );
}

// ============ ADD CLIENT MODAL ============
function AddClientModal({ token, onClose, onCreated }) {
  const [f, setF] = useState({
    name: "",
    phone: "",
    email: "",
    product: "AUTO",
    company: "",
    premium: "",
    eff_date: "",
    referred_by: "",
    original_notes: "",
    status: "active",
    source_month: "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target?.value ?? e });

  const submit = async () => {
    if (!f.name || !f.product) return toast.error("Name and product are required");
    setBusy(true);
    try {
      const payload = { ...f, premium: parseFloat(f.premium) || 0, source: "manual" };
      const c = await createClient(payload, token);
      onCreated(c);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed to add client");
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell title="Add client to book" subtitle="Bound policy · manual entry" onClose={onClose}>
      <FieldRow cols={2}>
        <FInput label="Client name *" value={f.name} onChange={set("name")} testid="add-client-name" />
        <FInput label="Phone" value={f.phone} onChange={set("phone")} testid="add-client-phone" />
      </FieldRow>
      <FieldRow>
        <FInput label="Email" type="email" value={f.email} onChange={set("email")} />
      </FieldRow>
      <FieldRow cols={2}>
        <Segment
          label="Product *"
          value={f.product}
          onChange={(v) => setF({ ...f, product: v })}
          options={[
            { k: "AUTO", l: "Auto" },
            { k: "HOME", l: "Home" },
            { k: "TENANT", l: "Tenant" },
            { k: "IRCA", l: "IRCA" },
            { k: "COMMERCIAL", l: "Commercial" },
          ]}
        />
        <FInput label="Carrier / company" value={f.company} onChange={set("company")} placeholder="e.g. AVIVA" />
      </FieldRow>
      <FieldRow cols={2}>
        <FInput label="Premium ($)" type="number" value={f.premium} onChange={set("premium")} />
        <FInput label="Effective date" type="date" value={f.eff_date} onChange={set("eff_date")} />
      </FieldRow>
      <FieldRow cols={2}>
        <Segment
          label="Status"
          value={f.status}
          onChange={(v) => setF({ ...f, status: v })}
          options={[
            { k: "active", l: "Active" },
            { k: "renewed", l: "Renewed" },
            { k: "rewritten", l: "Rewritten" },
            { k: "cancelled", l: "Cancelled" },
            { k: "not_renewed", l: "Not renewed" },
          ]}
        />
        <FInput label="Referred by" value={f.referred_by} onChange={set("referred_by")} />
      </FieldRow>
      <FieldRow>
        <label className="block text-xs uppercase tracking-wider text-bone-100/70 mb-2">Notes</label>
        <Textarea rows={2} value={f.original_notes} onChange={set("original_notes")} className="bg-forest-900 border-bone-100/15 text-bone-100" />
      </FieldRow>

      <ModalActions busy={busy} onClose={onClose} onSubmit={submit} testid="add-client-submit" label="Add client" />
    </ModalShell>
  );
}

// ============ MODAL PRIMITIVES ============
const ModalShell = ({ title, subtitle, onClose, children }) => (
  <div
    className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto"
    onClick={onClose}
  >
    <div
      className="bg-forest-800 border border-bone-100/15 p-6 md:p-8 w-full max-w-lg my-8"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="mb-6">
        <div className="font-serif text-2xl text-bone-100">{title}</div>
        <div className="overline text-bone-100/50 mt-1">{subtitle}</div>
      </div>
      {children}
    </div>
  </div>
);

const FieldRow = ({ cols = 1, children }) => (
  <div className={`grid ${cols === 2 ? "md:grid-cols-2" : "grid-cols-1"} gap-4 mb-4`}>{children}</div>
);

const FInput = ({ label, value, onChange, type = "text", placeholder = "", testid = "" }) => (
  <div>
    <label className="block text-xs uppercase tracking-wider text-bone-100/70 mb-2">{label}</label>
    <Input
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      data-testid={testid || undefined}
      className="bg-forest-900 border-bone-100/15 text-bone-100"
    />
  </div>
);

const Segment = ({ label, value, onChange, options }) => (
  <div>
    <label className="block text-xs uppercase tracking-wider text-bone-100/70 mb-2">{label}</label>
    <div className="flex gap-1 flex-wrap">
      {options.map((o) => (
        <button
          key={o.k}
          type="button"
          onClick={() => onChange(o.k)}
          className={`px-3 py-2 text-xs uppercase tracking-wider border transition-colors ${
            value === o.k
              ? "bg-terracotta-500 text-forest-900 border-terracotta-500"
              : "border-bone-100/20 text-bone-100/70 hover:border-bone-100/50"
          }`}
        >
          {o.l}
        </button>
      ))}
    </div>
  </div>
);

const ModalActions = ({ busy, onClose, onSubmit, label, testid }) => (
  <div className="flex gap-3 mt-6">
    <button
      onClick={onClose}
      className="flex-1 border border-bone-100/20 text-bone-100 py-2.5 hover:bg-bone-100/5 transition-colors"
    >
      Cancel
    </button>
    <button
      data-testid={testid}
      onClick={onSubmit}
      disabled={busy}
      className="flex-1 bg-terracotta-500 text-forest-900 py-2.5 hover:bg-terracotta-400 disabled:opacity-40 font-medium transition-colors"
    >
      {busy ? "Saving..." : label}
    </button>
  </div>
);

